/**
 * Finding the passages that bear on a claim.
 *
 * Two arms, both running entirely on this machine: BM25 over the passage's words,
 * and a cosine against a local hashed embedding from `embed.ts`. No second API
 * surface and no model download — a retrieval that called out would be a second
 * thing that can fail, a second thing to pay for, and a second place for the
 * user's own documents to travel to.
 *
 * The second arm is here because the first one is blind by construction. BM25
 * scores a passage on the words it shares with the claim, so a passage that says
 * the same thing in other words — "slower respiring mitochondria" against
 * "mitochondrial respiration slows" — scores exactly zero, and the check then
 * reports `nothing-found` over a corpus that held the answer. That is the worst
 * failure this layer has: not a wrong passage, which a reader can see, but a
 * silence that reads like evidence of absence.
 *
 * The arms are not equals, and the weights below say so. Keyword overlap is
 * CHECKABLE — the word is either in the passage or it is not, and the user can
 * see which. A cosine is not: it is a number about a paraphrase, and no one can
 * confirm it by looking. So the lexical arm decides the order and the semantic arm
 * rescues what the lexical arm could not see at all.
 *
 * The honesty requirement shapes the whole design. A top-k retriever always
 * returns k results, so a query with nothing to match still comes back with the
 * three least-irrelevant passages — and the model, handed passages, will find a
 * way to lean on them. So this scores COVERAGE of the claim's distinctive terms
 * as well as relevance, and returns NOTHING below a floor. "The sources provided
 * do not speak to this" has to be reachable, or grounding becomes a machine for
 * manufacturing support.
 */

import { acronymsOf, CHUNKER, type LoadedDoc } from './corpus'
import { cosine, embed, EMBED_VERSION } from './embed'
import { fnv1a64, foldText, SHINGLE_WORDS } from './similarity'

const K1 = 1.4
const B = 0.7
/** Passages offered to one check. */
export const K_MAX = 8
/** Total characters of passage text in one prompt. */
export const CHAR_BUDGET = 7000
/** Per document, so one long document cannot crowd out the rest of the corpus. */
export const PER_DOC = 4
/**
 * Below this share of the claim's distinctive terms, a passage is not about the
 * claim and is not offered at all.
 *
 * The question the floor asks is "is this passage about the claim?", and since
 * the embedding arrived there are two ways to answer yes — so a passage clears it
 * on this share OR on `SEMANTIC_FLOOR` below. What did NOT change is what happens
 * to a passage that clears neither: it is dropped, and its audit row still says
 * `below-coverage-floor`, because that is still the reason.
 */
export const COVERAGE_FLOOR = 0.28
/**
 * The cosine at which a passage counts as being about the claim even though it
 * shares none of its words.
 *
 * Calibrated against what `embed.ts` actually produces rather than picked for
 * roundness, and RE-calibrated once a longer corpus was measured against it.
 *
 * On short fixtures an unrelated passage scores 0.00–0.10 and a paraphrase
 * carrying no whole word of the claim scores 0.40, so 0.15 sat in the gap. On real
 * documents it does not: measured against a six-passage research summary and a
 * deep-research report on one subject, claims about STATINS, VITAMIN D and
 * MYCORRHIZAL FUNGI — none of which those documents mention — score 0.15, 0.19,
 * 0.20 and 0.26. Long domain prose is close to a great deal in the space, and
 * 0.15 admitted every one of them: three claims the documents do not speak to,
 * offered passages as though they did. That is the one failure this floor exists
 * to prevent, and the off-topic tests in `retrieve.test.ts` caught it.
 *
 * 0.30 sits in the measured gap instead — above the 0.26 an unrelated claim
 * reaches on long prose, below the 0.40 a genuine paraphrase reaches with no
 * whole word shared. The cost is the band between them: a paraphrase that is one
 * sentence buried in a long passage, which used to clear 0.15, now falls under
 * the floor unless its words also earn some lexical coverage. A missed passage is
 * the failure this app can live with; a manufactured one is not.
 *
 * It is used in TWO places — `matchedNothing` and `belowTheFloor` — and that is
 * deliberately one number, not two. Both are asking the same question, and a
 * passage that is "about the claim" enough to survive one gate and not the other
 * would be a distinction with nothing behind it.
 */
export const SEMANTIC_FLOOR = 0.3
/**
 * How the two arms are mixed. Both terms are normalised to 0…1 first: BM25 by the
 * best BM25 in this retrieval, the cosine by being a cosine already.
 *
 * 0.7 / 0.3, for the reason in the header — one arm is checkable and the other is
 * not. The consequence is worth stating in numbers, because it is what the tests
 * pin: the lexical term spans 0.7 of the final score and the semantic term, at the
 * 0.3–0.6 cosines a genuine match produces, spans about 0.1–0.2. So a passage with
 * ANY real keyword match outranks a paraphrase with none, and the paraphrase is
 * offered underneath it rather than in front of it. Raising the semantic weight
 * past about 0.45 would invert that, and put an unverifiable number in charge of a
 * ranking a reader is supposed to be able to check.
 */
export const HYBRID_BM25_WEIGHT = 0.7
export const HYBRID_SEMANTIC_WEIGHT = 0.3
/** Above the floor but below this, the match is reported as weak — in the prompt,
 *  so the model knows, and in the inspector, so the user does. */
const THIN_BELOW = 0.45
/**
 * How much of a passage has to appear, word triple for word triple, in a passage
 * already offered to the same check before it is a COPY of that passage and is not
 * offered again.
 *
 * The ranking is by the claim's words, and copies carry the claim's words: a press
 * release and three outlets that ran it verbatim are four passages that each match a
 * claim lifted from the release, and before this they took four of a check's
 * `K_MAX` slots while the paper and the independent report that decide the claim
 * sat under the cut (D319). D28 collapses copies among a refutation's web sources;
 * nothing collapsed them in what a check reads.
 *
 * Triples because `similarity.ts` already says why: three-word shingles make the
 * measure about phrasing and not about topic, so two documents on one subject share
 * their vocabulary and almost none of their triples. Containment rather than a
 * signature distance because a copy's first passage carries its own masthead and
 * headline, which moved `simHash` 12 to 14 bits from the original's on the
 * dense-packet tasks, past the 10 that syndication collapses at, while the share of
 * its triples found in the original's passage stayed above a half.
 *
 * Measured on the four dense-packet tasks and the four pilot tasks: passages of
 * distinct documents shared at most 0.33 of their triples; every copy shared at
 * least 0.52. Half sits in that gap. A miss here costs crowding, which is the
 * failure this exists to stop; a false merge costs a passage, and the kept passage
 * names every document it stands for, so a reader of the prompt still sees that the
 * other document said it.
 */
export const COPY_CONTAINMENT = 0.5
/** Fewer triples than this and a share is too coarse to call a copy by; only an
 *  identical passage, after folding, is one. */
export const COPY_MIN_TRIPLES = 12

/**
 * Words that carry no discriminating power in this corpus.
 *
 * Short by design. A large stoplist tuned on English prose starts removing terms
 * that matter in a technical claim — "field", "state", "order", "point" are all
 * load-bearing somewhere.
 */
const STOP = new Set(
  (
    'a an and are as at be been but by can could did do does for from had has have he her his if in into is it its' +
    ' may might must no not of on or our over own same she should so some such than that the their them then there these' +
    ' they this those through to too under until up was we were what when where which while who why will with would you your'
  ).split(' '),
)

/** The splitter itself, case untouched. `tokenise` folds case before it and
 *  `termsOf` after it, because only the second needs to know what was capitals. */
const words = (s: string): string[] =>
  s
    // Keep intra-word hyphens and decimal points: "room-temperature" and "0.2 nN"
    // are single terms, and splitting them loses exactly the specificity that
    // makes a claim checkable.
    .replace(/[^\p{L}\p{N}\s.\-–—]/gu, ' ')
    .split(/[\s–—]+/)
    .map((w) => w.replace(/^[.\-]+|[.\-]+$/g, ''))

/**
 * Exported for `link.ts`, which needs word-splitting to mean the same thing
 * there as it does here — the hyphen and decimal rules above are exactly as
 * load-bearing when the text is a search result as when it is your own document.
 * The two modules layer different STOPLISTS on top of one splitter rather than
 * carrying two splitters that can drift apart.
 *
 * Unnormalised on purpose. `link.ts` prints its terms to a reader, and a stem is
 * not a word anybody wrote; the normalisation below is this module's own layer.
 */
export const tokenise = (s: string): string[] =>
  words(s.toLowerCase()).filter((w) => w.length > 1 && !STOP.has(w))

/* ── normalisation ─────────────────────────────────────────────────────────
   A keyword search that cannot see that "smoking" and "smoke", or "lipids" and
   "lipid", are one word misses the passage that matters and reports that the
   documents do not speak to the claim — which is a false negative stated as a
   finding. So every term, in a passage and in a query alike, is folded before it
   is counted.

   The folding is Porter's step 1 (plurals, -ed, -ing) and his final-e rule,
   which step 1 needs so that "measure" and "measured" meet — and nothing past
   that. The later steps fold "general" into "generate" and "organ" into
   "organization", and a false match is worse than a miss here: it is support
   manufactured out of spelling. The same caution sets the edges: nothing with a
   digit, a decimal point or a letter outside a–z is touched, nothing shorter
   than four letters is, and a stem shorter than three is thrown back ("used"
   would otherwise become "us", which is a country as often as a pronoun). */

/** Porter's consonant: y is a vowel when a consonant precedes it. */
function consonant(w: string, i: number): boolean {
  const c = w[i]!
  if (c === 'a' || c === 'e' || c === 'i' || c === 'o' || c === 'u') return false
  return c === 'y' ? i === 0 || !consonant(w, i - 1) : true
}

/** Porter's measure: the m in [C](VC)^m[V]. */
function measure(w: string): number {
  let m = 0
  let i = 0
  while (i < w.length && consonant(w, i)) i++
  while (i < w.length) {
    while (i < w.length && !consonant(w, i)) i++
    if (i >= w.length) break
    while (i < w.length && consonant(w, i)) i++
    m++
  }
  return m
}

const hasVowel = (w: string): boolean => [...w].some((_, i) => !consonant(w, i))
const doubled = (w: string): boolean =>
  w.length > 1 && w.at(-1) === w.at(-2) && consonant(w, w.length - 1)
/** Consonant–vowel–consonant, the last not w, x or y: "hop", "rat", "smok". */
const cvc = (w: string): boolean =>
  w.length > 2 &&
  consonant(w, w.length - 3) &&
  !consonant(w, w.length - 2) &&
  consonant(w, w.length - 1) &&
  !'wxy'.includes(w.at(-1)!)

/** One lowercase a–z word through step 1 and step 5. */
function porter(word: string): string {
  // The Greek plurals first, so "pyrolyses" and "hypotheses" meet their singulars
  // rather than being read as "pyrolyse" + s. Only these two endings: "-oses" is
  // as often "doses" as it is "diagnoses".
  let w = word.replace(/(ys|es)es$/, '$1is')
  // 1a — plurals.
  if (w.endsWith('sses')) w = w.slice(0, -2)
  else if (w.endsWith('ies')) w = w.slice(0, -2)
  else if (!w.endsWith('ss') && w.endsWith('s')) w = w.slice(0, -1)
  // 1b — -ed and -ing, then the repair that puts back what the suffix took:
  // "rating" → "rat" → "rate", "hopping" → "hopp" → "hop".
  let cut = false
  if (w.endsWith('eed')) {
    if (measure(w.slice(0, -3)) > 0) w = w.slice(0, -1)
  } else if (w.endsWith('ed') && hasVowel(w.slice(0, -2))) {
    w = w.slice(0, -2)
    cut = true
  } else if (w.endsWith('ing') && hasVowel(w.slice(0, -3))) {
    w = w.slice(0, -3)
    cut = true
  }
  if (cut) {
    if (/(at|bl|iz)$/.test(w)) w += 'e'
    else if (doubled(w) && !'lsz'.includes(w.at(-1)!)) w = w.slice(0, -1)
    else if (measure(w) === 1 && cvc(w)) w += 'e'
  }
  // 1c — "study" and "studies" meet at "studi".
  if (w.endsWith('y') && hasVowel(w.slice(0, -1))) w = `${w.slice(0, -1)}i`
  // 5a — the final e, which is what lets "measure" meet "measured" while "rate"
  // stays away from "rat".
  if (w.endsWith('e')) {
    const s = w.slice(0, -1)
    const m = measure(s)
    if (m > 1 || (m === 1 && !cvc(s))) w = s
  }
  // 5b — "controll" (from "controlled") back to "control".
  if (w.endsWith('ll') && measure(w) > 1) w = w.slice(0, -1)
  return w
}

/**
 * One term, folded. `raw` is the word as written, because capitals are the one
 * thing that tells an acronym from a plural: "MNPs" is the plural of MNP and
 * folds to it, while "AIDS" and "SARS" are names that merely end in S and are
 * left alone.
 */
function fold(term: string, raw: string): string {
  if (/^[A-Z]{2,}s$/.test(raw)) return term.slice(0, -1)
  if (/^[A-Z]{2,5}$/.test(raw)) return term
  // A compound folds on its last part: "false-positives" meets "false-positive",
  // and "room-temperature" stays one term, which is the tokeniser's own rule.
  const at = term.lastIndexOf('-') + 1
  const tail = term.slice(at)
  if (tail.length < 4 || !/^[a-z]+$/.test(tail)) return term
  const stem = porter(tail)
  return stem.length < 3 ? term : term.slice(0, at) + stem
}

/**
 * A short form one of the documents defined, as the terms on each side of it.
 *
 * Emitted rather than substituted: wherever the long form is spelled out, the
 * short form's term is counted beside it, so a query naming "MNPs" finds the
 * passage that wrote "micro- and nanoplastics", and a query spelling it out still
 * finds the passage that only ever wrote the letters.
 */
interface Alias {
  long: readonly string[]
  short: string
}

/**
 * The terms a text is matched on: the shared splitter, the shared stoplist, the
 * folding above, and then any short forms the pool defined.
 *
 * Applied to the passages and to the query through this one function, because a
 * normalisation applied to one side only is a mismatch rather than a match.
 */
export function termsOf(s: string, aliases: readonly Alias[] = []): string[] {
  const out: string[] = []
  for (const raw of words(s)) {
    const w = raw.toLowerCase()
    if (w.length <= 1 || STOP.has(w)) continue
    const t = fold(w, raw)
    if (!STOP.has(t)) out.push(t)
  }
  if (!aliases.length) return out
  const byFirst = new Map<string, Alias[]>()
  for (const a of aliases) byFirst.set(a.long[0]!, [...(byFirst.get(a.long[0]!) ?? []), a])
  const emitted: string[] = []
  out.forEach((t, i) => {
    for (const a of byFirst.get(t) ?? []) {
      if (a.long.every((u, k) => out[i + k] === u)) emitted.push(a.short)
    }
  })
  return emitted.length ? [...out, ...emitted] : out
}

/**
 * The short forms the pool defines, keeping only the unambiguous ones.
 *
 * A short form spelled out two different ways — "PE" as polyethylene in one
 * document and as pulmonary embolism in another — is dropped in both, and so is
 * a long form given two short forms. Guessing which one a claim meant is exactly
 * the false match this layer must not make; an acronym left unexpanded costs a
 * miss and nothing more.
 */
function aliasesOf(docs: readonly LoadedDoc[]): Alias[] {
  const longs = new Map<string, Set<string>>()
  const shorts = new Map<string, Set<string>>()
  const found: Alias[] = []
  for (const d of docs) {
    for (const a of d.acronyms ?? acronymsOf(d.text)) {
      const short = termsOf(a.short)
      const long = termsOf(a.long)
      if (short.length !== 1 || !long.length || long.includes(short[0]!)) continue
      const key = long.join(' ')
      longs.set(short[0]!, (longs.get(short[0]!) ?? new Set()).add(key))
      shorts.set(key, (shorts.get(key) ?? new Set()).add(short[0]!))
      found.push({ long, short: short[0]! })
    }
  }
  const kept = new Map<string, Alias>()
  for (const a of found) {
    const key = a.long.join(' ')
    if (longs.get(a.short)!.size === 1 && shorts.get(key)!.size === 1) kept.set(key, a)
  }
  return [...kept.values()]
}

/**
 * What a check searches the corpus for: the node's label and its claim.
 *
 * The same two fields `liveQuery` builds the research rows' query from, for the
 * same reason — they are the node's own statement of what it asserts. Whole here,
 * where that one sends the label and a handful of the claim's words, because
 * nothing here leaves the machine and BM25 weighs a common word down on its own.
 *
 * The REASONING is deliberately not in it. Every query term is in the coverage
 * denominator, so a paragraph of the model's argument — whose vocabulary is its
 * own, not the claim's — dragged every passage under `COVERAGE_FLOOR`: a check on
 * a claim its documents discussed directly was offered none of them, while the
 * claim on its own matched three. `Try the search` in the Sources dialog builds
 * its query here too, so what it shows is what a check on that claim is shown.
 */
export const claimQuery = (label: string, claim: string): string =>
  `${label} ${claim}`.replace(/\s+/g, ' ').trim()

export interface Chunk {
  docId: string
  docTitle: string
  /** 1-based, document-level. The durable reference. */
  n: number
  text: string
}

export interface Scored extends Chunk {
  /**
   * The hybrid, and the only number anything ranks on. Comparable within one
   * retrieval and meaningless across two, because the lexical half is normalised
   * by the best BM25 this call saw.
   */
  score: number
  coverage: number
  /**
   * The raw BM25, before that normalisation, and the cosine — carried on the row
   * rather than recomputed, because this is the same audit obligation the
   * dispositions below carry. A row that gives only the mix cannot say which arm
   * put it there, and "the model was shown a passage that shares no word with the
   * claim" is precisely the thing an audit has to be able to notice. The two
   * together also make the row self-contained: the largest `bm25` among the
   * candidates IS the divisor, so `score` can be rebuilt by hand from the rows.
   */
  bm25: number
  semantic: number
  /**
   * The passages folded into this one as copies of it (`COPY_CONTAINMENT`), set
   * only on an offered passage. The prompt names them, because "four documents
   * carry this text" is a fact about its independence that slots were never the
   * right way to convey.
   */
  copies?: { docId: string; docTitle: string; n: number }[]
}

/**
 * Why a scored corpus candidate did or did not reach the model prompt.
 *
 * This is audit data, not a second ranking policy. `retrieve` assigns it in the
 * same loop that enforces the limits, so an audit cannot later guess which of
 * several caps discarded a passage.
 *
 * `zero-score` is the one whose meaning widened when the second arm arrived: it
 * now means the passage matched on NEITHER arm — no query term in it and a cosine
 * under `SEMANTIC_FLOOR` — rather than "BM25 returned nothing". The string is
 * unchanged because `engine/checkAudit.ts` writes it into a stored certificate as
 * a code, and a renamed code would make an old certificate unreadable to say
 * something the old certificate already said.
 */
export type RetrievalDisposition =
  | 'offered'
  | 'query-empty'
  | 'zero-score'
  | 'below-coverage-floor'
  | 'copy-of-offered'
  | 'capped-result-count'
  | 'capped-per-document'
  | 'capped-character-budget'

export interface RetrievalCandidate extends Scored {
  /** One-based rank among every chunk, before any threshold or cap. */
  rank: number
  disposition: RetrievalDisposition
}

export interface Retrieval {
  passages: Scored[]
  /** Every chunk considered, including rows rejected by a threshold or cap. */
  candidates: RetrievalCandidate[]
  /**
   * Call-local number → chunk. Keys are `index + 1` of `passages`.
   *
   * The prompt prints these numbers and only these numbers, and resolution reads
   * the same map — so printing and resolving cannot drift. A prompt that also
   * showed a passage's position in its document would invite a citation under the
   * wrong numbering, which would resolve to real text that says something else.
   */
  table: Map<number, Chunk>
  match: 'none' | 'thin' | 'ok'
  offered: number
  bestCoverage: number
  docsAtCheck: number
}

const empty = (docs: number, candidates: RetrievalCandidate[] = []): Retrieval => ({
  passages: [],
  candidates,
  table: new Map(),
  match: 'none',
  offered: 0,
  bestCoverage: 0,
  docsAtCheck: docs,
})

/**
 * The cache's bound, and what it costs: `EMBED_CACHE_MAX × EMBED_DIM` floats is
 * about 4 MB, several times over what a corpus at its own ceiling can produce. It
 * is not here to ration a working set, then — it is here so that fetched research
 * documents, whose ids are new on every check, cannot grow the map without limit
 * across a long session.
 */
const EMBED_CACHE_MAX = 2048

/**
 * Vectors already computed, so that a second call over the same corpus does not
 * embed it again.
 *
 * Worth having because every call embeds EVERY passage — it has to, since the
 * whole point is to score the passages BM25 gave nothing, and since every
 * candidate needs a row in the audit. On a full corpus that is a few hundred
 * passages at about 0.3 ms each on the first call, and nothing on every call after
 * it. The live probe in the sources dialog re-runs on each keystroke, and that is
 * the surface this is for.
 *
 * KEYED ON IDS, NOT ON OBJECTS. A `LoadedDoc` is rebuilt from storage on every
 * check, so a `WeakMap` on the document or on the chunk array would miss every
 * single time. The key carries which document, which passage of it, which chunker
 * split it and which version of `embed` produced it, so changing any of those
 * makes the old entry simply never asked for again.
 *
 * AND THE PASSAGE'S OWN TEXT, which the four above do not imply. A document id
 * does NOT name one immutable text here: `engine/liveSources.ts` fetches a record
 * as its abstract and then, for open-access papers, asks once more and REPLACES
 * that abstract with the paper's own body — under the same identity, because it
 * is the same work. Keyed on the id alone, the full text would have been ranked
 * on the abstract's vector, silently, and the passage the model was handed would
 * not be the passage that was scored.
 *
 * It was the passage's LENGTH, and that was the wrong guard for a reason worth
 * keeping: the argument for it was that an abstract and a full text are never the
 * same number of characters, which is true of the DOCUMENT and is not what the key
 * held. It held one PASSAGE's length, and the splitter emits a passage of exactly
 * `MAX_PASSAGE` whenever it finds no sentence end to cut at — so two different
 * passages at the same index of the same swapped document collide by construction
 * rather than by luck. A stale vector there is the quiet failure this whole layer
 * exists downstream of: a passage ranked, admitted or rejected on text that is not
 * its own.
 *
 * So the text signs itself. `fnv1a64` is the tree's one FNV — not a second copy —
 * over the passage, and it costs a fraction of what embedding the same passage
 * costs, paid on hits as well as misses. That is a real cost and it buys the one
 * thing a length cannot: the entry served is an entry computed from THIS text.
 */
const embedCache = new Map<string, Float32Array>()

/**
 * Eviction is oldest-INSERTED and not least-recently-used, deliberately: an LRU
 * would have to re-order the map on every read, on a path that reads it hundreds
 * of times per call, to manage a ceiling nothing is expected to reach.
 */
function embedChunk(docId: string, chunkIndex: number, text: string): Float32Array {
  const key = `${docId}:${chunkIndex}:${fnv1a64(text)}:${CHUNKER}:${EMBED_VERSION}`
  const cached = embedCache.get(key)
  if (cached) return cached

  const vector = embed(text)
  if (embedCache.size >= EMBED_CACHE_MAX) {
    const oldest = embedCache.keys().next().value
    if (oldest !== undefined) embedCache.delete(oldest)
  }
  embedCache.set(key, vector)
  return vector
}

/**
 * Whether a candidate matched the claim AT ALL, on its words or on its shape.
 *
 * A predicate rather than an inline condition because it is asked TWICE — once to
 * label the audit row before the loop, once to discard the candidate inside it —
 * and those two answers must not be able to differ. Written out twice, one copy
 * would decide what the model sees and the other what the audit says it saw,
 * which is the one disagreement this file cannot have.
 */
const matchedNothing = (candidate: Scored): boolean =>
  candidate.bm25 <= 0 && candidate.semantic < SEMANTIC_FLOOR

/** The coverage floor, with its second door — see `COVERAGE_FLOOR`. Note that this
 *  subsumes `matchedNothing`: a passage with no BM25 has no coverage either. Both
 *  are kept because they are two different reasons and the audit row names one. */
const belowTheFloor = (candidate: Scored): boolean =>
  candidate.coverage < COVERAGE_FLOOR && candidate.semantic < SEMANTIC_FLOOR

/** A passage's word triples over its folded text, as `simHash` shingles it. */
function triplesOf(text: string): Set<string> {
  const words = foldText(text).split(' ').filter(Boolean)
  const out = new Set<string>()
  for (let i = 0; i + SHINGLE_WORDS <= words.length; i++) {
    out.add(words.slice(i, i + SHINGLE_WORDS).join(' '))
  }
  return out
}

/**
 * The offered passage `candidate` copies, or null: the same text after folding, or
 * at least `COPY_CONTAINMENT` of its triples inside one offered passage. One
 * direction only — the candidate's triples in the offered passage — so a longer
 * passage that quotes an offered one and goes on to say more is not a copy of it.
 */
function copyOf(
  candidate: Scored,
  kept: readonly Scored[],
  triples: (s: Scored) => Set<string>,
): Scored | null {
  const folded = foldText(candidate.text)
  for (const k of kept) if (foldText(k.text) === folded) return k
  const mine = triples(candidate)
  if (mine.size < COPY_MIN_TRIPLES) return null
  for (const k of kept) {
    const theirs = triples(k)
    let shared = 0
    for (const t of mine) if (theirs.has(t)) shared++
    if (shared / mine.size >= COPY_CONTAINMENT) return k
  }
  return null
}

export function retrieve(query: string, docs: LoadedDoc[]): Retrieval {
  if (!docs.length) return empty(0)

  // Flatten to chunks, keeping the document-level index as the durable number.
  const chunks: Chunk[] = []
  for (const d of docs) {
    d.chunks.forEach((text, i) => {
      chunks.push({ docId: d.id, docTitle: d.title, n: i + 1, text })
    })
  }
  if (!chunks.length) return empty(docs.length)

  // One set of short forms for the whole pool, applied to every passage and to the
  // query alike — see `aliasesOf` for why an ambiguous one is dropped outright.
  const aliases = aliasesOf(docs)
  const qTerms = [...new Set(termsOf(query, aliases))]
  if (!qTerms.length) {
    return empty(
      docs.length,
      // No lexical query means no semantic one either: a claim made entirely of
      // stopwords is as empty to the embedding as it is to BM25, and running the
      // second arm over it would only give the emptiness a score.
      chunks.map((chunk, index) => ({
        ...chunk,
        score: 0,
        coverage: 0,
        bm25: 0,
        semantic: 0,
        rank: index + 1,
        disposition: 'query-empty',
      })),
    )
  }

  const tokens = chunks.map((c) => termsOf(c.text, aliases))
  const lens = tokens.map((t) => t.length)
  const avg = lens.reduce((a, b) => a + b, 0) / lens.length || 1

  // Document frequency over passages, for IDF.
  const df = new Map<string, number>()
  tokens.forEach((t) => {
    for (const w of new Set(t)) df.set(w, (df.get(w) ?? 0) + 1)
  })
  const N = chunks.length
  const idf = (w: string) => {
    const n = df.get(w) ?? 0
    return Math.log(1 + (N - n + 0.5) / (n + 0.5))
  }

  /**
   * Coverage: what share of the CLAIM's weight a passage accounts for.
   *
   * Every query term is weighted by its corpus IDF and every query term is in the
   * denominator — including the ones the corpus has never seen, which score the
   * highest IDF of all and can never be in the numerator. That asymmetry is the
   * whole mechanism, and it is deliberately conservative in both directions:
   *
   *  - Score coverage only over terms the corpus HAS, and a corpus containing one
   *    incidental word of a claim scores 1.00 — an off-topic query comes back a
   *    perfect match.
   *  - Take the highest IDF as a threshold for which terms "count", and absent
   *    terms define that threshold, so nothing can ever reach it and every query
   *    comes back empty.
   *
   * Both of those were real, and both were the same mistake: treating a subset of
   * the terms as the thing being covered. Weighting all of them continuously has no
   * threshold to get wrong.
   */
  const weights = new Map(qTerms.map((w) => [w, idf(w)]))
  const totalWeight = qTerms.reduce((a, w) => a + (weights.get(w) ?? 0), 0) || 1

  // Once, for the whole call. The passages' vectors are cached across calls and
  // the query's is not, because the query is what changes between them.
  const queryVector = embed(query)

  const measured = chunks.map((c, i) => {
    const t = tokens[i]!
    const tf = new Map<string, number>()
    for (const w of t) tf.set(w, (tf.get(w) ?? 0) + 1)
    let bm25 = 0
    let covered = 0
    for (const w of qTerms) {
      const f = tf.get(w) ?? 0
      if (f === 0) continue
      const w_ = weights.get(w) ?? 0
      bm25 += w_ * ((f * (K1 + 1)) / (f + K1 * (1 - B + B * (lens[i]! / avg))))
      covered += w_
    }
    // `c.n` is the passage's 1-based number within its OWN document, so `n - 1` is
    // the index the cache key wants. The flattened position `i` shifts with
    // whatever documents came before it, so the same passage would key differently
    // from one call to the next and the cache would never hit.
    const semantic = cosine(queryVector, embedChunk(c.docId, c.n - 1, c.text))
    return { ...c, bm25, semantic, coverage: covered / totalWeight }
  })

  /**
   * The mix. BM25 is unbounded above and depends on the corpus's IDF, so it is
   * scaled by the best score in this retrieval rather than by any absolute number
   * — which does mean the lexical term is a RANK-relative quantity and the
   * semantic term is not. That asymmetry is the point: the best keyword match
   * always takes the full 0.7 and the arms stay in the proportion the constants
   * name, whatever the corpus's vocabulary happens to make BM25 look like.
   *
   * With no lexical match anywhere the divisor would be zero, and the lexical term
   * is then zero for everybody rather than NaN for everybody — a corpus that
   * answers a claim only in other words ranks on the second arm alone, which is
   * the case this whole change exists for.
   */
  const maxBm25 = measured.reduce((m, s) => Math.max(m, s.bm25), 0)
  const scored: Scored[] = measured.map((m) => ({
    ...m,
    score:
      HYBRID_BM25_WEIGHT * (maxBm25 > 0 ? m.bm25 / maxBm25 : 0) +
      HYBRID_SEMANTIC_WEIGHT * m.semantic,
  }))

  /**
   * Still the LEXICAL best, and deliberately not the hybrid.
   *
   * `bestCoverage` is printed as "matching N% of the claim's distinctive terms",
   * and a cosine is not a share of anything — folding one into this number would
   * make the sentence false. The consequence is that a retrieval carried entirely
   * by the second arm reports `thin`, so the prompt tells the model the match is
   * weak and the dialog tells the user. That is the honest reading: not one word
   * of the claim appears in what was offered.
   */
  const bestCoverage = scored.reduce((m, s) => Math.max(m, s.coverage), 0)

  // Rank EVERY candidate first. Filtering first made the model input correct but
  // erased the rows an audit needs in order to explain that input.
  const ranked = scored.sort((a, b) => b.score - a.score)
  const candidates: RetrievalCandidate[] = ranked.map((candidate, index) => ({
    ...candidate,
    rank: index + 1,
    disposition: matchedNothing(candidate) ? 'zero-score' : 'below-coverage-floor',
  }))
  const kept: Scored[] = []
  const perDoc = new Map<string, number>()
  let chars = 0
  const shingled = new Map<Scored, Set<string>>()
  const triples = (s: Scored) => {
    let t = shingled.get(s)
    if (!t) shingled.set(s, (t = triplesOf(s.text)))
    return t
  }
  for (const candidate of candidates) {
    if (matchedNothing(candidate)) continue
    if (belowTheFloor(candidate)) continue
    // Before the caps, so a copy spends none of them: its slot, its document's
    // share and its characters go to the next passage instead (D319).
    const original = copyOf(candidate, kept, triples)
    if (original) {
      candidate.disposition = 'copy-of-offered'
      ;(original.copies ??= []).push({
        docId: candidate.docId,
        docTitle: candidate.docTitle,
        n: candidate.n,
      })
      continue
    }
    if (kept.length >= K_MAX) {
      candidate.disposition = 'capped-result-count'
      continue
    }
    const s: Scored = candidate
    const used = perDoc.get(s.docId) ?? 0
    if (used >= PER_DOC) {
      candidate.disposition = 'capped-per-document'
      continue
    }
    if (chars + s.text.length > CHAR_BUDGET && kept.length > 0) {
      candidate.disposition = 'capped-character-budget'
      continue
    }
    candidate.disposition = 'offered'
    kept.push(s)
    perDoc.set(s.docId, used + 1)
    chars += s.text.length
  }

  const table = new Map<number, Chunk>()
  kept.forEach((c, i) => table.set(i + 1, c))

  return {
    passages: kept,
    candidates,
    table,
    match: kept.length === 0 ? 'none' : bestCoverage < THIN_BELOW ? 'thin' : 'ok',
    offered: kept.length,
    bestCoverage,
    docsAtCheck: docs.length,
  }
}
